import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './AdminUserDetail.module.css';
import { userApi } from '../../services/userApi';

interface UserDetailParams {
  userId: string;
}

interface Firm {
  Firm_ID: string;
  abbrev: string;
  name?: string;
  logo?: string;
  status?: number;
}

interface City {
  City_ID: string;
  name: string;
  Country?: {
    name: string;
    abbrev?: string;
  };
  country?: {
    name: string;
    abbrev?: string;
  };
}

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  picture?: string;
  created_at: string;
  updated_at: string;
  last_login?: string;
  linkedin?: string;
  profile?: string;
  gender?: string;
  is_scim?: boolean;
  department?: string;
  employee_group?: string;
  firm?: Firm;
  City_ID?: string;
  Firm_ID?: string;
  verification?: number;
}

const Admin_User_Detail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<boolean>(true); // Start in edit mode since we're matching the template
  const [formData, setFormData] = useState<Partial<User>>({});
  const [firms, setFirms] = useState<Firm[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [firmSearch, setFirmSearch] = useState<string>('');
  const [citySearch, setCitySearch] = useState<string>('');
  const [showFirmDropdown, setShowFirmDropdown] = useState<boolean>(false);
  const [showCityDropdown, setShowCityDropdown] = useState<boolean>(false);
  const navigate = useNavigate();
  const isMountedRef = useRef<boolean>(true);
  const firmDropdownRef = useRef<HTMLDivElement>(null);
  const cityDropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to extract error message from API response
  const getErrorMessage = async (error: any): Promise<string> => {
    if (error?.response?.json) {
      try {
        const errorData = await error.response.json();
        return errorData.detail || errorData.message || 'An error occurred';
      } catch {
        return error.message || 'An error occurred';
      }
    } else if (error?.detail) {
      return error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };

  // Avoid navigating too quickly which can cause ResizeObserver issues
  const navigateWithDelay = (path: string) => {
    // Short delay to allow cleanup
    setTimeout(() => {
      if (isMountedRef.current) {
        navigate(path);
      }
    }, 50);
  };

  useEffect(() => {
    if (userId) {
      fetchUser();
      fetchFirms();
      fetchCities();
    }
    
    // Cleanup function to handle unmounting gracefully
    return () => {
      isMountedRef.current = false;
    };
  }, [userId]);

  useEffect(() => {
    // Set image preview when user data is loaded
    if (user?.picture) {
      setImagePreview(`data:image/png;base64,${user.picture}`);
    }
  }, [user]);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (firmDropdownRef.current && !firmDropdownRef.current.contains(event.target as Node)) {
        setShowFirmDropdown(false);
      }
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target as Node)) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter firms based on search
  const filteredFirms = firms.filter(firm => 
    (firm.name?.toLowerCase() || '').includes(firmSearch.toLowerCase()) ||
    firm.abbrev.toLowerCase().includes(firmSearch.toLowerCase())
  );

  // Filter cities based on search
  const filteredCities = cities.filter(city => {
    const countryData = city.country || city.Country;
    return city.name.toLowerCase().includes(citySearch.toLowerCase()) ||
      (countryData?.name?.toLowerCase() || '').includes(citySearch.toLowerCase());
  });

  // Get selected firm name for display
  const getSelectedFirmName = () => {
    if (!formData.Firm_ID) return '';
    const firm = firms.find(f => f.Firm_ID === formData.Firm_ID);
    if (!firm) return '';
    const displayName = firm.name || firm.abbrev;
    return firm.status !== 1 ? `${displayName} (inactive)` : displayName;
  };

  // Get selected city name for display
  const getSelectedCityName = () => {
    if (!formData.City_ID) return '';
    const city = cities.find(c => c.City_ID === formData.City_ID);
    if (!city) return '';
    const countryData = city.country || city.Country;
    const countryName = countryData?.name || '';
    const countryAbbrev = countryData?.abbrev || '';
    return `${city.name}${countryName ? ` (${countryName})` : ''}${countryAbbrev ? ` [${countryAbbrev}]` : ''}`;
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const userData = await userApi.getUserById(userId!);
      setUser(userData);
      setFormData(userData);
            
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      const errorMessage = await getErrorMessage(error);
      setError(`Failed to load user data: ${errorMessage}`);
      setLoading(false);
      toast.error(`Failed to load user data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const fetchFirms = async () => {
    try {
      const firmsData = await userApi.getFirmsLookup();
      setFirms(firmsData);
    } catch (error) {
      console.error('Error fetching firms:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Failed to load firms data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const fetchCities = async () => {
    try {
      const citiesData = await userApi.getCitiesLookup();
      setCities(citiesData);
    } catch (error) {
      console.error('Error fetching cities:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Failed to load cities data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const isChecked = (e.target as HTMLInputElement).checked;
      if (id === 'inputVerification') {
        setFormData(prev => ({
          ...prev,
          verification: isChecked ? 1 : 0
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [id.replace('input', '').toLowerCase()]: isChecked
        }));
      }
    } else if (id === 'inputfirm') {
      setFormData(prev => ({
        ...prev,
        Firm_ID: value
      }));
    } else if (id === 'inputcity') {
      setFormData(prev => ({
        ...prev,
        City_ID: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [id.replace('input', '').toLowerCase()]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    // Prevent any form of page reload
    e.preventDefault();
    e.stopPropagation();
    
    if (!userId) return;
    
    try {
      // Show a loading indicator in the button or elsewhere if needed
      const updatedUser = await userApi.updateUser(userId, formData);
      
      // Update local state with the updated user data
      // Merge the response with current user to preserve all fields
      setUser(prev => ({
        ...prev,
        ...updatedUser,
        // Ensure formData fields are also updated
        ...formData
      } as User));
      
      // Show success toast
      toast.success('User updated successfully', {
        position: "top-right",
        autoClose: 4000
      });
      
      // No need to call fetchUser() as we already have the updated data
      // Just update any other pieces of state if needed
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Failed to update user: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const handleDelete = async () => {
    if (!userId) {
      return;
    }
    
    // Instead of window.confirm, use toast.warn with a custom UI for confirmation
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this user?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await userApi.deleteUser(userId);
                
                // Force navigation using window.location as backup
                if (navigate) {
                  navigate('/admin/users', { replace: true });
                } else {
                  window.location.href = '/admin/users';
                }
                
                // Show success toast after navigation
                setTimeout(() => {
                  toast.success('User deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                  });
                }, 100);
              } catch (error) {
                console.error('Error deleting user:', error);
                const errorMessage = await getErrorMessage(error);
                toast.error(`Failed to delete user: ${errorMessage}`, {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Extract base64 data
        const fullBase64 = reader.result?.toString();
        const base64String = fullBase64?.split(',')[1];
        if (base64String) {
          setFormData(prev => ({
            ...prev,
            picture: base64String
          }));
          // Set image preview with full base64 string for immediate display
          setImagePreview(fullBase64 || null);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePicture = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      picture: ''
    }));
    // Reset file input
    const fileInput = document.getElementById('inputGroupFile02') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (loading) {
    return <div className={`container mt-5 ${styles.loadingContainer}`}><div className="spinner-border" role="status"></div></div>;
  }

  if (error || !user) {
    return <div className={`container mt-5 alert alert-danger ${styles.errorAlert}`}>{error || 'User not found'}</div>;
  }

  return (
    <div className={styles.root}>
       <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className={`breadcrumb `}>
            <li className={`breadcrumb-item `}>
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className={`breadcrumb-item `}>
              <Link to="/admin/users">User Management</Link>
            </li>
            <li className={`breadcrumb-item active `} aria-current="page">
              {user.title} {user.firstname} {user.lastname}
            </li>
          </ol>
        </nav>

        {/* Roles Management Button */}
        <div className="row mb-3">
          <div className="col d-flex justify-content-end">
            <Link 
              className={`btn btn-sm btn-primary ${styles.rolesButton}`} 
              to={`/admin/users/${user.User_ID}/roles`} 
            >
              Roles Management
            </Link>
          </div>
        </div>

        {/* SCIM Warning */}
        {user.is_scim && (
          <div className={`row ${styles.scimWarning}`}>
            <div className="alert alert-warning" role="alert">
              This user is managed via SCIM. Some fields cannot be edited and must be changed by the system administrator.
            </div>
          </div>
        )}

        <div className="container-xl px-4 mt-4">
          <form id="profileForm" onSubmit={(e) => e.preventDefault()} noValidate>
            <div className="row">
              <div className="col-xl-4">
                {/* Profile picture card */}
                <div className={`card mb-4 mb-xl-0 ${styles.card}`}>
                  <div className={`card-header ${styles.cardHeader}`}>Profile Picture</div>
                  <div className={`card-body text-center ${styles.cardBody}`}>
                    {/* Profile picture image */}
                    {imagePreview ? (
                      <img 
                        id="profileImage" 
                        className={`img-account-profile rounded-circle mb-2 ${styles.profilePicture}`}
                        src={imagePreview} 
                        alt="Profile Picture" 
                      />
                    ) : user?.picture ? (
                      <img 
                        id="profileImage" 
                        className={`img-account-profile rounded-circle mb-2 ${styles.profilePicture}`}
                        src={`data:image/png;base64,${user.picture}`} 
                        alt="Profile Picture" 
                      />
                    ) : (
                      <img 
                        id="profileImage" 
                        className={`img-account-profile rounded-circle mb-2 ${styles.profilePicture}`}
                        src="/static/images/user-profile-icon.png" 
                        alt="Default Profile Picture" 
                      />
                    )}

                    {/* Profile picture help block */}
                    <div className={`small font-italic text-muted mb-4 ${styles.profileUploadInfo}`}>JPG or PNG no larger than 5 MB</div>

                    {/* Hidden file input */}
                    <input 
                      type="file" 
                      id="fileInput" 
                      accept="image/png, image/jpeg" 
                      className="d-none"
                      onChange={handleFileUpload} 
                    />

                    {/* Profile picture upload button */}
                    <button 
                      id="uploadButton" 
                      className="btn btn-primary w-100" 
                      type="button"
                      onClick={() => document.getElementById('fileInput')?.click()}
                    >
                      <i className="bi bi-upload me-2"></i>
                      Upload new image
                    </button>

                    {/* Remove picture button */}
                    {imagePreview && (
                      <button 
                        type="button"
                        className="btn btn-outline-danger w-100 mt-2" 
                        onClick={handleRemovePicture}
                      >
                        <i className="bi bi-trash me-2"></i>
                        Remove Picture
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-xl-8">
                {/* Account details card */}
                <div className={`card mb-4 ${styles.card}`}>
                  <div className={`card-header ${styles.cardHeader}`}>Account Details</div>
                  <div className={`card-body ${styles.cardBody}`}>
                    {/* First name and Last name */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Form Group (first name) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputFirstname">First name</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputFirstname" 
                          type="text"
                          placeholder="Enter first name" 
                          value={formData.firstname || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        />
                      </div>
                      {/* Form Group (last name) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputLastname">Last name</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputLastname" 
                          type="text"
                          placeholder="Enter last name" 
                          value={formData.lastname || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        />
                      </div>
                    </div>
                    
                    {/* Title and Verification */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Form Group (title) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputTitle">Title</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputTitle" 
                          type="text"
                          placeholder="Enter title (e.g. Dr., Prof.)" 
                          value={formData.title || ''} 
                          onChange={handleInputChange}
                          disabled={user.is_scim}
                        />
                      </div>

                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <div className="form-check mt-4">
                          <input 
                            className="form-check-input" 
                            type="checkbox" 
                            id="inputVerification" 
                            checked={formData.verification === 1}
                            onChange={handleInputChange}
                          />
                          <label className={`form-check-label ${styles.formLabel}`} htmlFor="inputVerification">Verification</label>
                        </div>
                      </div>
                    </div>

                    {/* Email address */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Form Group (email address) */}
                      <div className={`col-md-12 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputMail">Email address</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputMail" 
                          type="email"
                          placeholder="Enter email address" 
                          value={formData.mail || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        />
                      </div>
                    </div>

                    {/* Firm */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      <div className={`col-md-12 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputfirm">Firm</label>
                        <div ref={firmDropdownRef} style={{ position: 'relative' }}>
                          <input
                            type="text"
                            className={`form-control ${styles.formControl}`}
                            placeholder="Search firms..."
                            value={formData.Firm_ID ? getSelectedFirmName() : firmSearch}
                            onChange={(e) => {
                              setFirmSearch(e.target.value);
                              setShowFirmDropdown(true);
                              if (!e.target.value) {
                                setFormData(prev => ({ ...prev, Firm_ID: undefined }));
                              }
                            }}
                            onFocus={() => setShowFirmDropdown(true)}
                            disabled={user?.is_scim}
                          />
                          {showFirmDropdown && !user?.is_scim && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              maxHeight: '200px',
                              overflowY: 'auto',
                              backgroundColor: 'white',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                              zIndex: 1000,
                              marginTop: '0.25rem'
                            }}>
                              {filteredFirms.map(firm => (
                                <div
                                  key={firm.Firm_ID}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, Firm_ID: firm.Firm_ID }));
                                    setFirmSearch('');
                                    setShowFirmDropdown(false);
                                  }}
                                >
                                  {firm.name || firm.abbrev}{firm.status !== 1 ? ' (inactive)' : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* LinkedIn and Profile */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputLinkedin">LinkedIn</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputLinkedin" 
                          type="url"
                          placeholder="Enter LinkedIn URL" 
                          value={formData.linkedin || ''} 
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputProfile">Profile</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputProfile" 
                          type="text"
                          placeholder="Enter profile" 
                          value={formData.profile || ''} 
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Job Title and Gender */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputJobtitle">Jobtitle</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputJobtitle" 
                          type="text"
                          placeholder="Enter job title" 
                          value={formData.jobtitle || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        />
                      </div>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputGender">Gender</label>
                        <select 
                          className={`form-control ${styles.formControl}`} 
                          id="inputGender"
                          value={formData.gender || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        >
                          <option value="">Select a gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Diverse">Diverse</option>
                        </select>
                      </div>
                    </div>

                    {/* City */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      <div className={`col-md-12 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputcity">City</label>
                        <div ref={cityDropdownRef} style={{ position: 'relative' }}>
                          <input
                            type="text"
                            className={`form-control ${styles.formControl}`}
                            placeholder="Search cities..."
                            value={formData.City_ID ? getSelectedCityName() : citySearch}
                            onChange={(e) => {
                              setCitySearch(e.target.value);
                              setShowCityDropdown(true);
                              if (!e.target.value) {
                                setFormData(prev => ({ ...prev, City_ID: undefined }));
                              }
                            }}
                            onFocus={() => setShowCityDropdown(true)}
                            disabled={user?.is_scim}
                          />
                          {showCityDropdown && !user?.is_scim && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              maxHeight: '200px',
                              overflowY: 'auto',
                              backgroundColor: 'white',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                              zIndex: 1000,
                              marginTop: '0.25rem'
                            }}>
                              {filteredCities.map(city => {
                                const countryData = city.country || city.Country;
                                return (
                                  <div
                                    key={city.City_ID}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #f0f0f0'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, City_ID: city.City_ID }));
                                      setCitySearch('');
                                      setShowCityDropdown(false);
                                    }}
                                  >
                                    {city.name}{countryData ? ` (${countryData.name})` : ''}{countryData?.abbrev ? ` [${countryData.abbrev}]` : ''}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>


                    <hr />

                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Form Group (department) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputDepartment">Department</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputDepartment" 
                          type="text"
                          placeholder="Enter department" 
                          value={formData.department || ''} 
                          onChange={handleInputChange}
                          disabled={user.is_scim}
                        />
                      </div>
                      {/* Form Group (employee group) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputEmployee_group">Employee Group</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputEmployee_group" 
                          type="text"
                          placeholder="Enter employee group" 
                          value={formData.employee_group || ''} 
                          onChange={handleInputChange}
                          disabled={user.is_scim}
                        />
                      </div>
                    </div>

                    <hr />

                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Creation, Update and Last Login Dates */}
                      <div className={`col-md-4 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`}>Created At</label>
                        <p className={styles.formControlStatic}>{formatDate(user.created_at)}</p>
                      </div>
                      <div className={`col-md-4 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`}>Updated At</label>
                        <p className={styles.formControlStatic}>{formatDate(user.updated_at)}</p>
                      </div>
                      <div className={`col-md-4 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`}>Last Login At</label>
                        <p className={styles.formControlStatic}>{user.last_login ? formatDate(user.last_login) : 'Never'}</p>
                      </div>
                    </div>

                    {/* Save and Delete buttons */}
                    <div className={styles.formButtons}>
                      <button 
                        className={`btn btn-primary me-md-2 ${styles.btnPrimary}`} 
                        type="button" 
                        onClick={handleSubmit}
                      >
                        Save changes
                      </button>
                      <button 
                        className={`btn btn-danger ${styles.btnDanger}`} 
                        type="button"
                        onClick={handleDelete}
                      >
                        Delete User
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
          </div>
  );
};

export default Admin_User_Detail;
